import { createSignal, type Accessor } from "solid-js";

/** What is drawn on the slides while presenting: pen lines and highlighter swipes, kept per slide key. Points are on
 *  the 1280 by 720 design stage, so the same line lies on the same words in every window whatever its size. Each
 *  screen saves its own steps (markup_saver.ts); a screen on another device takes the others' from the server. */

export type MarkTool = "pen" | "highlighter";
export type MarkColor = "red" | "yellow" | "green" | "blue";

export const MARK_COLOR_ORDER: MarkColor[] = ["red", "yellow", "green", "blue"];

// `swatch` is the same color as a whole Tailwind class, written out so Tailwind sees it.
export const MARK_COLORS: Record<MarkColor, { label: string; hex: string; swatch: string }> = {
  red: { label: "Red", hex: "#E5484D", swatch: "bg-[#E5484D]" },
  yellow: { label: "Yellow", hex: "#FFD60A", swatch: "bg-[#FFD60A]" },
  green: { label: "Green", hex: "#30A46C", swatch: "bg-[#30A46C]" },
  blue: { label: "Blue", hex: "#0090FF", swatch: "bg-[#0090FF]" },
};

export const MARK_WIDTH: Record<MarkTool, number> = { pen: 6, highlighter: 30 };

export type Stroke = {
  id: string;
  tool: MarkTool;
  color: MarkColor;
  /** x, y, x, y... in stage units. */
  points: number[];
};

type Sheet = Record<string, Stroke[]>;

/** One step on one slide, as the server stores it: strokes drawn (or brought back by Undo) and strokes taken off. */
export type MarkupChange = { slide: string; add: Stroke[]; remove: string[] };

/** What one side tells the other: a step on a slide (strokes added and taken off, by id, so two screens drawing at
 *  the same moment both keep both lines), the stroke being drawn (null once it ends), a newly opened screen asking
 *  for what is already drawn, and the answer. */
export type MarkupMessage =
  | { change: MarkupChange }
  | { drawing: { slide: string; stroke: Stroke | null } }
  | { hello: true }
  | { sheet: Sheet };

export function isMarkupMessage(value: unknown): value is MarkupMessage {
  if (typeof value !== "object" || value === null) return false;
  return ["change", "drawing", "hello", "sheet"].some((kind) => kind in value);
}

export type Markup = {
  strokes: (slide: string) => Stroke[];
  /** The strokes still being drawn on the slide, here and on the other side. */
  drawing: (slide: string) => Stroke[];
  draw: (slide: string, stroke: Stroke | null) => void;
  add: (slide: string, stroke: Stroke) => void;
  /** Takes the strokes off. `gesture` "more" keeps one eraser drag as one step for Undo. */
  erase: (slide: string, ids: string[], gesture: "start" | "more") => void;
  clear: (slide: string) => void;
  undo: (slide: string) => void;
  canUndo: (slide: string) => boolean;
  /** Drawn anywhere at all. */
  any: Accessor<boolean>;
  /** Strokes drawn before this screen opened (a saved show, a record's layer). Sent nowhere and saved nowhere. */
  seed: (slides: { slide: string; strokes: Stroke[] }[]) => void;
  /** Wipes every slide, for a new show: the last show's lines stay on its own record. */
  reset: () => void;
  /** Calls back with each step taken on this screen, never one that came from another: what a saver listens to. */
  onOwnChange: (listener: (change: MarkupChange) => void) => void;
  /** Steps taken on another device, as the server read them: merged in, keeping whatever this screen drew meanwhile,
   *  and passed to the other screen on this device. */
  merge: (changes: MarkupChange[]) => void;
  /** The sync's side: messages out, and what came in. */
  link: (send: (message: MarkupMessage) => void) => void;
  receive: (message: MarkupMessage) => void;
};

let made = 0;
export function newStrokeId(): string {
  made += 1;
  return `${Date.now().toString(36)}-${made}-${Math.random().toString(36).slice(2, 8)}`;
}

// One step Undo takes back: the strokes it drew and the strokes it took off. Undo only ever takes back this screen's
// own steps, and leaves alone whatever the other side drew since.
type Step = { added: string[]; removed: Stroke[] };

export function createMarkup(): Markup {
  const [sheet, setSheet] = createSignal<Sheet>({});
  const [steps, setSteps] = createSignal<Record<string, Step[]>>({});
  const [own, setOwn] = createSignal<Record<string, Stroke>>({});
  const [theirs, setTheirs] = createSignal<Record<string, Stroke>>({});
  let send: (message: MarkupMessage) => void = () => {};
  const listeners: ((change: MarkupChange) => void)[] = [];
  // The slide the eraser drag under way has already taken something off.
  let erasing: string | null = null;

  const strokes = (slide: string) => sheet()[slide] ?? [];
  const stepsOf = (slide: string) => steps()[slide] ?? [];

  // A step from anywhere, onto what this screen holds: what it takes off goes, what it adds joins at the end unless
  // already here. => whether anything moved.
  const apply = ({ slide, add, remove }: MarkupChange) => {
    const before = strokes(slide);
    const kept = before.filter((stroke) => !remove.includes(stroke.id));
    const added = add.filter((stroke) => !kept.some((other) => other.id === stroke.id));
    if (kept.length === before.length && added.length === 0) return false;
    setSheet((all) => ({ ...all, [slide]: [...kept, ...added] }));
    return true;
  };
  // A step on this screen: here and on the other screen, and to the saver.
  const commit = (slide: string, next: Stroke[]) => {
    const before = strokes(slide);
    const change: MarkupChange = {
      slide,
      add: next.filter((stroke) => !before.some((other) => other.id === stroke.id)),
      remove: before
        .filter((stroke) => !next.some((other) => other.id === stroke.id))
        .map((stroke) => stroke.id),
    };
    if (!apply(change)) return;
    send({ change });
    listeners.forEach((listener) => listener(change));
  };
  const record = (slide: string, step: Step, joinLast = false) =>
    setSteps((all) => {
      const list = all[slide] ?? [];
      const last = list.at(-1);
      if (joinLast && last)
        return {
          ...all,
          [slide]: [
            ...list.slice(0, -1),
            { added: last.added, removed: [...last.removed, ...step.removed] },
          ],
        };
      return { ...all, [slide]: [...list, step] };
    });

  const withoutKey = (all: Record<string, Stroke>, slide: string) =>
    Object.fromEntries(Object.entries(all).filter(([key]) => key !== slide));

  return {
    strokes,
    drawing: (slide) => [own()[slide], theirs()[slide]].filter((stroke) => stroke !== undefined),
    draw: (slide, stroke) => {
      setOwn((all) => (stroke ? { ...all, [slide]: stroke } : withoutKey(all, slide)));
      send({ drawing: { slide, stroke } });
    },
    add: (slide, stroke) => {
      setOwn((all) => withoutKey(all, slide));
      record(slide, { added: [stroke.id], removed: [] });
      commit(slide, [...strokes(slide), stroke]);
    },
    erase: (slide, ids, gesture) => {
      if (gesture === "start") erasing = null;
      const removed = strokes(slide).filter((stroke) => ids.includes(stroke.id));
      if (removed.length === 0) return;
      // A drag that started on nothing has no step yet: its first hit starts one.
      record(slide, { added: [], removed }, erasing === slide);
      erasing = slide;
      commit(
        slide,
        strokes(slide).filter((stroke) => !ids.includes(stroke.id)),
      );
    },
    clear: (slide) => {
      if (strokes(slide).length === 0) return;
      record(slide, { added: [], removed: strokes(slide) });
      commit(slide, []);
    },
    undo: (slide) => {
      const step = stepsOf(slide).at(-1);
      if (!step) return;
      setSteps((all) => ({ ...all, [slide]: stepsOf(slide).slice(0, -1) }));
      const kept = strokes(slide).filter((stroke) => !step.added.includes(stroke.id));
      const back = step.removed.filter((stroke) => !kept.some((other) => other.id === stroke.id));
      commit(slide, [...kept, ...back]);
    },
    canUndo: (slide) => stepsOf(slide).length > 0,
    any: () => Object.values(sheet()).some((list) => list.length > 0),
    seed: (slides) =>
      setSheet(Object.fromEntries(slides.map((entry) => [entry.slide, entry.strokes]))),
    reset: () => {
      setSheet({});
      setSteps({});
      setOwn({});
      setTheirs({});
    },
    onOwnChange: (listener) => {
      listeners.push(listener);
    },
    merge: (changes) => {
      for (const change of changes) if (apply(change)) send({ change });
    },
    link: (sender) => {
      send = sender;
    },
    receive: (message) => {
      if ("change" in message) {
        const { slide, add } = message.change;
        if (add.length > 0) setTheirs((all) => withoutKey(all, slide));
        apply(message.change);
      } else if ("drawing" in message) {
        const { slide, stroke } = message.drawing;
        setTheirs((all) => (stroke ? { ...all, [slide]: stroke } : withoutKey(all, slide)));
      } else if ("hello" in message) {
        if (Object.keys(sheet()).length > 0) send({ sheet: sheet() });
      } else if ("sheet" in message) {
        // A screen opened later takes what was drawn before it, and keeps any slide it has drawn on itself.
        setSheet((all) => ({ ...message.sheet, ...all }));
      }
    },
  };
}

/** The SVG path through a stroke's points, smoothed through their midpoints. One point draws a dot. */
export function strokePath(points: number[]): string {
  const at = (i: number) => `${round(points[i * 2]!)} ${round(points[i * 2 + 1]!)}`;
  const count = Math.floor(points.length / 2);
  if (count === 0) return "";
  if (count === 1) return `M${at(0)}L${at(0)}`;
  let path = `M${at(0)}`;
  for (let i = 1; i < count - 1; i++) {
    const midX = (points[i * 2]! + points[i * 2 + 2]!) / 2;
    const midY = (points[i * 2 + 1]! + points[i * 2 + 3]!) / 2;
    path += `Q${at(i)} ${round(midX)} ${round(midY)}`;
  }
  return `${path}L${at(count - 1)}`;
}

/** The strokes passing within `radius` of the eraser's sweep, from (fromX, fromY) to (x, y), measured to the edge of
 *  each line. A sweep from where the finger last was catches a thin line crossed between two pointer events. */
export function strokesNear(
  strokes: Stroke[],
  x: number,
  y: number,
  radius: number,
  fromX = x,
  fromY = y,
): string[] {
  return strokes
    .filter((stroke) => {
      const reach = radius + MARK_WIDTH[stroke.tool] / 2;
      const { points } = stroke;
      const last = points.length === 2 ? 0 : points.length - 2;
      for (let i = 0; i === 0 || i < last; i += 2) {
        const ax = points[i]!;
        const ay = points[i + 1]!;
        const bx = points[i + 2] ?? ax;
        const by = points[i + 3] ?? ay;
        if (segmentsDistance(fromX, fromY, x, y, ax, ay, bx, by) <= reach) return true;
      }
      return false;
    })
    .map((stroke) => stroke.id);
}

function segmentsDistance(
  px: number,
  py: number,
  qx: number,
  qy: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
) {
  if (segmentsCross(px, py, qx, qy, ax, ay, bx, by)) return 0;
  return Math.min(
    segmentDistance(px, py, ax, ay, bx, by),
    segmentDistance(qx, qy, ax, ay, bx, by),
    segmentDistance(ax, ay, px, py, qx, qy),
    segmentDistance(bx, by, px, py, qx, qy),
  );
}

function segmentsCross(
  px: number,
  py: number,
  qx: number,
  qy: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
) {
  const side = (ox: number, oy: number, ux: number, uy: number, vx: number, vy: number) =>
    Math.sign((ux - ox) * (vy - oy) - (uy - oy) * (vx - ox));
  return (
    side(px, py, qx, qy, ax, ay) * side(px, py, qx, qy, bx, by) < 0 &&
    side(ax, ay, bx, by, px, py) * side(ax, ay, bx, by, qx, qy) < 0
  );
}

function segmentDistance(px: number, py: number, ax: number, ay: number, bx: number, by: number) {
  const dx = bx - ax;
  const dy = by - ay;
  const length = dx * dx + dy * dy;
  const t = length === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / length));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

const round = (value: number) => Math.round(value * 10) / 10;
